import { useState, useRef, useEffect } from 'react';
import { useActiveHomepageVideo } from '@/hooks/useHomepageVideo';

const FALLBACK_THUMBNAIL = 'https://dhpifehbegozqmcrwlsw.supabase.co/storage/v1/object/public/media-files/homepage-videos/thumbnails/1757336096900-thumb-Farm_Dog.jpg';

interface HomeVideoSectionProps {
  /** Delay DB query until parent is ready (e.g. after hero paint). Defaults to true. */
  enabled?: boolean;
}

const HomeVideoSection = ({ enabled = true }: HomeVideoSectionProps) => {
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: activeVideo } = useActiveHomepageVideo({
    enabled,
  });

  // Detect scroll or 7-second delay to load video
  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setShouldLoadVideo(true);
    }, 7000);

    const handleScroll = () => setShouldLoadVideo(true);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleVideoError = () => {
    setVideoError(true);
    console.error('Video failed to load');
  };

  const handleThumbnailLoad = () => {
    setThumbnailLoaded(true);
  };

  const videoUrl = activeVideo?.video_url ?? '';
  const sources = activeVideo ? [
    { src: activeVideo.video_url_webm || videoUrl.replace(/\.mp4(\?.*)?$/, '.webm$1'), type: 'video/webm' },
    { src: activeVideo.video_url_mp4 || videoUrl, type: 'video/mp4' },
  ].filter((source) => source.src) : [];

  return (
    <div ref={containerRef} className="relative w-full h-64 lg:h-80 bg-gray-900 rounded-lg overflow-hidden shadow-lg">
      <>
        {/* Thumbnail — low priority, below the fold */}
        <img
          src={activeVideo?.thumbnail_url ?? FALLBACK_THUMBNAIL}
          alt="Video thumbnail"
          className="w-full h-full object-cover"
          onLoad={handleThumbnailLoad}
          fetchPriority="low"
          loading="lazy"
          width="800"
          height="450"
          decoding="async"
        />

        {/* Play overlay */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="bg-black/30 rounded-full p-3">
            <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>

        {/* Video loaded only after thumbnail and delay */}
        {thumbnailLoaded && shouldLoadVideo && sources.length > 0 && !videoError && (
          <video
            ref={videoRef}
            poster={activeVideo?.thumbnail_url ?? FALLBACK_THUMBNAIL}
            muted
            loop
            playsInline
            autoPlay
            preload="none"
            className="w-full h-full object-cover absolute top-0 left-0"
            onError={handleVideoError}
          >
            {sources.map((s) => (
              <source key={s.type} src={s.src} type={s.type} />
            ))}
            <p className="text-white p-4">Your browser does not support the video tag.</p>
          </video>
        )}

        {videoError && (
          <div className="absolute inset-0 flex items-center justify-center text-white">
            Video failed to load
          </div>
        )}
      </>
    </div>
  );
};

export default HomeVideoSection;
